/**
 * FarmRoadThresholdsManager
 *
 * Service de gestion des seuils critiques paramétrables pour chaque ferme
 * Les paramètres sont stockés dans PostgreSQL (backend) et partagés entre
 * tous les utilisateurs ayant accès à la ferme.
 *
 * Fonctionnalités :
 * - CRUD des seuils par indicateur (via API backend)
 * - Valeurs par défaut selon la culture
 * - Validation des seuils
 * - Évaluation des mesures par rapport aux seuils
 */

import { getFirebaseToken } from '../firebase';

const BackEndUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';

class FarmRoadThresholdsManager {
  constructor() {
    // Seuils par défaut pour différentes cultures
    this.defaultThresholds = {
      framboises: {
        temperature: {
          min_critical: 10,
          min_warning: 15,
          optimal_min: 18,
          optimal_max: 24,
          max_warning: 28,
          max_critical: 32,
          unit: '°C',
          description: 'Température optimale pour framboises: 18-24°C jour, 12-15°C nuit',
          duration_type: 'daily'
        },
        humidity: {
          min_critical: 40,
          min_warning: 55,
          optimal_min: 60,
          optimal_max: 80,
          max_warning: 85,
          max_critical: 95,
          unit: '%',
          description: 'Taux d\'humidité de l\'air. Influence la transpiration et le risque de maladies.',
          duration_type: 'daily'
        },
        soil_moisture: {
          min_critical: 20,
          min_warning: 30,
          optimal_min: 40,
          optimal_max: 70,
          max_warning: 80,
          max_critical: 90,
          unit: '%',
          description: 'Teneur en eau du substrat. Détermine la disponibilité de l\'eau pour les racines.',
          duration_type: 'daily'
        },
        light_intensity: {
          min_critical: 5000,
          min_warning: 10000,
          optimal_min: 15000,
          optimal_max: 40000,
          max_warning: 60000,
          max_critical: 80000,
          unit: 'lux',
          description: 'Luminosité optimale: 15000-40000 lux',
          duration_type: 'daily'
        },
        co2: {
          min_critical: 300,
          min_warning: 400,
          optimal_min: 800,
          optimal_max: 1200,
          max_warning: 1500,
          max_critical: 2000,
          unit: 'ppm',
          description: 'Concentration de CO2 pendant le jour. Substrat essentiel pour la croissance.',
          duration_type: 'daily'
        },
        daily_light_integral: {
          min_critical: 8,
          min_warning: 10,
          optimal_min: 12,
          optimal_max: 18,
          max_warning: 20,
          max_critical: 25,
          unit: 'mol/m²/jour',
          description: 'Quantité totale de lumière reçue sur 24h. Détermine le potentiel de croissance quotidienne.',
          duration_type: 'daily'
        },
        vpd_diurnal: {
          min_critical: 0.3,
          min_warning: 0.5,
          optimal_min: 0.8,
          optimal_max: 1.2,
          max_warning: 1.4,
          max_critical: 1.8,
          unit: 'kPa',
          description: 'Écart d\'humidité air-plante pendant le jour. Contrôle la transpiration et le risque de maladies.',
          duration_type: 'daily'
        },
        temperature_cold_stress: {
          min_critical: 0,
          min_warning: 0,
          optimal_min: 0,
          optimal_max: 1,
          max_warning: 2,
          max_critical: 4,
          unit: 'heures',
          description: 'Durée quotidienne d\'exposition au froid. En dessous de 10°C, la croissance ralentit.',
          duration_type: 'daily'
        },
        temperature_heat_stress: {
          min_critical: 0,
          min_warning: 0,
          optimal_min: 0,
          optimal_max: 1,
          max_warning: 3,
          max_critical: 5,
          unit: 'heures',
          description: 'Durée quotidienne d\'exposition à forte chaleur. Au-delà de 30°C, la croissance se bloque.',
          duration_type: 'daily'
        },
        temperature_amplitude: {
          min_critical: 0,
          min_warning: 0,
          optimal_min: 8,
          optimal_max: 15,
          max_warning: 18,
          max_critical: 22,
          unit: '°C',
          description: 'Écart de température entre jour et nuit. Important pour la qualité des fruits.',
          duration_type: 'daily'
        },
        radiation_mean_diurnal: {
          min_critical: 100,
          min_warning: 150,
          optimal_min: 200,
          optimal_max: 400,
          max_warning: 500,
          max_critical: 600,
          unit: 'µmol/m²/s',
          description: 'Intensité lumineuse moyenne pendant le jour. Indique l\'énergie disponible pour la croissance.',
          duration_type: 'daily'
        },
        co2_saturation: {
          min_critical: 0,
          min_warning: 0,
          optimal_min: 0,
          optimal_max: 1,
          max_warning: 3,
          max_critical: 5,
          unit: 'heures',
          description: 'Durée avec CO2 très élevé. Au-delà de 1400 ppm, l\'absorption devient inefficace.',
          duration_type: 'daily'
        },
        sunlight_duration: {
          min_critical: 4,
          min_warning: 6,
          optimal_min: 8,
          optimal_max: 14,
          max_warning: 16,
          max_critical: 18,
          unit: 'heures',
          description: 'Nombre d\'heures avec lumière suffisante. Détermine la durée d\'activité des plantes.',
          duration_type: 'daily'
        },
        gdd: {
          min_critical: 0,
          min_warning: 50,
          optimal_min: 100,
          optimal_max: 2500,
          max_warning: 3000,
          max_critical: 3500,
          unit: 'GDD',
          description: 'Growing Degree Days pour framboises (base 4.5°C) - Débourrement: 100-200, Floraison: 800-1200, Fructification: 1500-2500',
          duration_type: 'daily'
        }
      },
      tomates: {
        temperature: {
          min_critical: 12,
          min_warning: 16,
          optimal_min: 20,
          optimal_max: 25,
          max_warning: 30,
          max_critical: 35,
          unit: '°C',
          description: 'Température optimale pour tomates: 20-25°C'
        },
        humidity: {
          min_critical: 50,
          min_warning: 60,
          optimal_min: 65,
          optimal_max: 75,
          max_warning: 80,
          max_critical: 90,
          unit: '%',
          description: 'Humidité relative optimale: 65-75%'
        },
        soil_moisture: {
          min_critical: 25,
          min_warning: 35,
          optimal_min: 45,
          optimal_max: 75,
          max_warning: 85,
          max_critical: 95,
          unit: '%',
          description: 'Humidité du substrat optimale: 45-75%'
        },
        light_intensity: {
          min_critical: 8000,
          min_warning: 15000,
          optimal_min: 20000,
          optimal_max: 50000,
          max_warning: 70000,
          max_critical: 90000,
          unit: 'lux',
          description: 'Luminosité optimale: 20000-50000 lux'
        },
        co2: {
          min_critical: 350,
          min_warning: 500,
          optimal_min: 1000,
          optimal_max: 1400,
          max_warning: 1800,
          max_critical: 2500,
          unit: 'ppm',
          description: 'Concentration CO2 optimale: 1000-1400 ppm'
        },
        daily_light_integral: {
          min_critical: 10,
          min_warning: 12,
          optimal_min: 15,
          optimal_max: 25,
          max_warning: 30,
          max_critical: 35,
          unit: 'mol/m²/jour',
          description: 'DLI optimal pour tomates: 15-25 mol/m²/jour pour croissance vigoureuse et fructification'
        },
        vpd_diurnal: {
          min_critical: 0.4,
          min_warning: 0.6,
          optimal_min: 0.8,
          optimal_max: 1.4,
          max_warning: 1.6,
          max_critical: 2.0,
          unit: 'kPa',
          description: 'VPD diurne optimal pour tomates: 0.8-1.4 kPa pour transpiration efficace et prévention maladies fongiques'
        }
      },
      generic: {
        temperature: {
          min_critical: 5,
          min_warning: 10,
          optimal_min: 15,
          optimal_max: 30,
          max_warning: 35,
          max_critical: 40,
          unit: '°C',
          description: 'Température ambiante',
          duration_type: 'daily'
        },
        humidity: {
          min_critical: 30,
          min_warning: 40,
          optimal_min: 50,
          optimal_max: 80,
          max_warning: 85,
          max_critical: 95,
          unit: '%',
          description: 'Humidité relative',
          duration_type: 'daily'
        },
        soil_moisture: {
          min_critical: 15,
          min_warning: 25,
          optimal_min: 35,
          optimal_max: 75,
          max_warning: 85,
          max_critical: 95,
          unit: '%',
          description: 'Humidité du sol',
          duration_type: 'daily'
        },
        light_intensity: {
          min_critical: 1000,
          min_warning: 5000,
          optimal_min: 10000,
          optimal_max: 50000,
          max_warning: 70000,
          max_critical: 100000,
          unit: 'lux',
          description: 'Intensité lumineuse',
          duration_type: 'daily'
        },
        co2: {
          min_critical: 300,
          min_warning: 400,
          optimal_min: 600,
          optimal_max: 1200,
          max_warning: 1500,
          max_critical: 2000,
          unit: 'ppm',
          description: 'Concentration en CO2',
          duration_type: 'daily'
        },
        vpd: {
          min_critical: 0.3,
          min_warning: 0.5,
          optimal_min: 0.8,
          optimal_max: 1.2,
          max_warning: 1.5,
          max_critical: 2.0,
          unit: 'kPa',
          description: 'Déficit de pression de vapeur (VPD) - optimal: 0.8-1.2 kPa',
          duration_type: 'daily'
        },
        dew_point: {
          min_critical: 5,
          min_warning: 10,
          optimal_min: 15,
          optimal_max: 20,
          max_warning: 25,
          max_critical: 30,
          unit: '°C',
          description: 'Point de rosée - dépend de la température et humidité',
          duration_type: 'daily'
        },
        daily_light_integral: {
          min_critical: 8,
          min_warning: 10,
          optimal_min: 12,
          optimal_max: 20,
          max_warning: 25,
          max_critical: 30,
          unit: 'mol/m²/jour',
          description: 'Intégrale lumineuse quotidienne (DLI)',
          duration_type: 'daily'
        },
        sunlight_duration: {
          min_critical: 8,
          min_warning: 10,
          optimal_min: 12,
          optimal_max: 16,
          max_warning: 18,
          max_critical: 20,
          unit: 'heures',
          description: 'Durée d\'ensoleillement effectif (PAR > 100 µmol/m²/s)',
          duration_type: 'daily'
        },
        vpd_diurnal: {
          min_critical: 0.3,
          min_warning: 0.5,
          optimal_min: 0.8,
          optimal_max: 1.2,
          max_warning: 1.5,
          max_critical: 2.0,
          unit: 'kPa',
          description: 'VPD optimal diurne',
          duration_type: 'daily'
        },
        vpd_nocturnal: {
          min_critical: 0.1,
          min_warning: 0.2,
          optimal_min: 0.3,
          optimal_max: 0.6,
          max_warning: 0.8,
          max_critical: 1.0,
          unit: 'kPa',
          description: 'VPD optimal nocturne',
          duration_type: 'daily'
        },
        temperature_cold_stress: {
          min_critical: 0,
          min_warning: 0,
          optimal_min: 0,
          optimal_max: 1,
          max_warning: 2,
          max_critical: 4,
          unit: 'heures',
          description: 'Durée quotidienne d\'exposition au froid. En dessous de 10°C, la croissance ralentit.',
          duration_type: 'daily'
        },
        temperature_heat_stress: {
          min_critical: 0,
          min_warning: 0,
          optimal_min: 0,
          optimal_max: 1,
          max_warning: 3,
          max_critical: 5,
          unit: 'heures',
          description: 'Durée quotidienne d\'exposition à forte chaleur. Au-delà de 30°C, la croissance se bloque.',
          duration_type: 'daily'
        },
        temperature_amplitude: {
          min_critical: 0,
          min_warning: 0,
          optimal_min: 8,
          optimal_max: 15,
          max_warning: 18,
          max_critical: 22,
          unit: '°C',
          description: 'Écart de température entre jour et nuit. Important pour la qualité des fruits.',
          duration_type: 'daily'
        },
        radiation_mean_diurnal: {
          min_critical: 100,
          min_warning: 150,
          optimal_min: 200,
          optimal_max: 400,
          max_warning: 500,
          max_critical: 600,
          unit: 'µmol/m²/s',
          description: 'Intensité lumineuse moyenne pendant le jour. Indique l\'énergie disponible pour la croissance.',
          duration_type: 'daily'
        },
        co2_saturation: {
          min_critical: 0,
          min_warning: 0,
          optimal_min: 0,
          optimal_max: 1,
          max_warning: 3,
          max_critical: 5,
          unit: 'heures',
          description: 'Durée avec CO2 très élevé. Au-delà de 1400 ppm, l\'absorption devient inefficace.',
          duration_type: 'daily'
        },
        gdd: {
          min_critical: 0,
          min_warning: 50,
          optimal_min: 100,
          optimal_max: 2500,
          max_warning: 3000,
          max_critical: 3500,
          unit: 'GDD',
          description: 'Growing Degree Days (base 4.5°C)',
          duration_type: 'daily'
        }
      }
    };

    // Métadonnées de culture (coefficient Kc, protection)
    // Séparées des seuils d'indicateurs pour éviter erreurs API
    this.cropMetadata = {
      framboises: {
        cropCoefficient: 0.85, // Kc - Coefficient cultural FAO pour framboises
        isCoveredCrop: true    // Par défaut sous serre/tunnel
      },
      tomates: {
        cropCoefficient: 1.15, // Kc - Coefficient cultural FAO pour tomates
        isCoveredCrop: true    // Par défaut sous serre/tunnel
      },
      generic: {
        cropCoefficient: 0.8,  // Kc par défaut
        isCoveredCrop: true    // Par défaut sous serre
      }
    };

    // DEBUG: Vérifier que tous les seuils sont bien chargés au démarrage
    console.log('[DEBUG Constructor] Seuils framboises chargés:', Object.keys(this.defaultThresholds.framboises));
    console.log('[DEBUG Constructor] Nombre total de seuils framboises:', Object.keys(this.defaultThresholds.framboises).length);
  }

  // =========================================================================
  // NORMALISATION DES CLÉS D'INDICATEURS
  // =========================================================================

  /**
   * Normalise les clés d'indicateurs pour correspondre aux seuils configurés
   * @param {string} key - Clé d'indicateur brute (ex: "RADIATION_INTENSITY_INSIDE")
   * @returns {string} Clé normalisée (ex: "light_intensity")
   */
  _normalizeIndicatorKey(key) {
    const keyLower = key.toLowerCase();

    // Indicateurs techniques non-agricoles à ignorer (pas d'alerte nécessaire)
    const technicalIndicators = ['battery_voltage', 'barometric_pressure_inside'];
    if (technicalIndicators.some(tech => keyLower.includes(tech))) {
      return null; // Retourner null pour les ignorer silencieusement
    }

    // Mapping des variantes vers les clés standards
    const mappings = {
      // Température
      'temperature': 'temperature',
      'temp': 'temperature',
      'air_temperature': 'temperature',

      // Humidité
      'humidity': 'humidity',
      'hum': 'humidity',
      'relative_humidity': 'humidity',
      'rh': 'humidity',

      // VPD (Déficit de pression de vapeur)
      'estimated_vpd_inside': 'vpd',
      'vpd': 'vpd',

      // Point de rosée
      'dewpoint': 'dew_point',
      'dewpoint_inside': 'dew_point',
      'dew_point': 'dew_point',

      // Humidité du sol
      'soil_moisture': 'soil_moisture',
      'moisture': 'soil_moisture',
      'soil_humidity': 'soil_moisture',

      // Lumière
      'light_intensity': 'light_intensity',
      'light': 'light_intensity',
      'par_intensity': 'light_intensity',
      'par': 'light_intensity',
      'radiation_intensity_inside': 'light_intensity',
      'radiation_intensity': 'light_intensity',
      'radiation': 'light_intensity',

      // CO2
      'co2_level': 'co2',
      'co2': 'co2',
      'carbon_dioxide': 'co2',

      // Pression
      'pressure': 'pressure',
      'atmospheric_pressure': 'pressure',
      'barometric_pressure': 'pressure'
    };

    // Chercher une correspondance directe
    if (mappings[keyLower]) {
      return mappings[keyLower];
    }

    // Chercher une correspondance partielle
    for (const [variant, standard] of Object.entries(mappings)) {
      if (keyLower.includes(variant)) {
        return standard;
      }
    }

    // Si aucune correspondance, retourner la clé originale en minuscules
    return keyLower;
  }

  // =========================================================================
  // UTILITAIRES HTTP
  // =========================================================================

  async _headers() {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    try {
      const token = await getFirebaseToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.warn('Impossible de récupérer le token Firebase:', error);
    }

    return headers;
  }

  async _handleResponse(response, context = '') {
    if (response.ok) {
      return await response.json();
    }

    let errorMessage = `Thresholds API [${context}] — HTTP ${response.status}`;

    try {
      const errorBody = await response.json();
      if (errorBody.message) {
        errorMessage += `: ${errorBody.message}`;
      } else if (errorBody.error) {
        errorMessage += `: ${errorBody.error}`;
      }
    } catch {
      errorMessage += `: ${response.statusText}`;
    }

    console.error(errorMessage);
    throw new Error(errorMessage);
  }

  // =========================================================================
  // RÉCUPÉRATION DES SEUILS (depuis PostgreSQL via API)
  // =========================================================================

  /**
   * Récupère les seuils configurés pour une ferme depuis PostgreSQL
   * @param {string|number} farmId - ID de la ferme
   * @returns {Promise<Object>} Configuration des seuils
   */
  async getThresholds(farmId) {
    if (!farmId) {
      console.error('farmId requis');
      return this.getDefaultThresholds('generic');
    }

    try {
      const response = await fetch(`${BackEndUrl}/api/farmroad/farms/${farmId}/settings`, {
        method: 'GET',
        headers: await this._headers()
      });

      const data = await this._handleResponse(response, `GET /farms/${farmId}/settings`);

      if (data.success && data.settings) {
        console.log('Seuils récupérés pour la ferme:', farmId);

        const culture = data.settings.culture || 'generic';
        const savedThresholds = data.settings.thresholds;

        // Migration automatique : ajouter les seuils manquants depuis le template
        const templateThresholds = this.defaultThresholds[culture] || this.defaultThresholds.generic;
        const migratedThresholds = { ...savedThresholds };
        let needsMigration = false;

        // Parcourir tous les seuils du template
        Object.keys(templateThresholds).forEach(key => {
          if (!savedThresholds[key]) {
            console.log(`Migration: ajout du seuil manquant "${key}" pour la culture ${culture}`);
            migratedThresholds[key] = templateThresholds[key];
            needsMigration = true;
          }
        });

        // Si des seuils ont été ajoutés, sauvegarder automatiquement la migration
        if (needsMigration) {
          console.log(`Migration automatique effectuée pour ${Object.keys(templateThresholds).length - Object.keys(savedThresholds).length} seuil(s)`);

          // Sauvegarder les seuils migrés (sans attendre pour ne pas bloquer)
          this.saveThresholds(farmId, {
            culture,
            objective: data.settings.objective || '',
            cropCoefficient: data.settings.cropCoefficient !== undefined
              ? data.settings.cropCoefficient
              : this.getCropCoefficient(culture),
            isCoveredCrop: data.settings.isCoveredCrop !== undefined
              ? data.settings.isCoveredCrop
              : (this.cropMetadata[culture]?.isCoveredCrop ?? true),
            thresholds: migratedThresholds,
            customIndicators: data.settings.customIndicators || {}
          }).catch(err => {
            console.warn('Erreur lors de la sauvegarde automatique des seuils migrés:', err);
          });
        }

        // Récupérer les métadonnées de culture depuis les settings ou depuis cropMetadata
        const cropCoefficient = data.settings.cropCoefficient !== undefined
          ? data.settings.cropCoefficient
          : this.getCropCoefficient(culture);

        const isCoveredCrop = data.settings.isCoveredCrop !== undefined
          ? data.settings.isCoveredCrop
          : (this.cropMetadata[culture]?.isCoveredCrop ?? true);

        return {
          farmId,
          culture,
          objective: data.settings.objective || '',
          cropCoefficient,
          isCoveredCrop,
          thresholds: migratedThresholds,
          customIndicators: data.settings.customIndicators || {},
          updatedAt: data.settings.updated_at,
          createdAt: data.settings.created_at,
          isDefault: false,
          wasMigrated: needsMigration
        };
      } else {
        // Retourner les seuils par défaut si non configurés
        console.log('Aucun seuil configuré, utilisation des valeurs par défaut');
        return this.getDefaultThresholds('generic');
      }
    } catch (error) {
      console.error('Erreur récupération seuils:', error);
      // En cas d'erreur, retourner les valeurs par défaut
      return this.getDefaultThresholds('generic');
    }
  }

  /**
   * Obtient les seuils par défaut pour une culture (côté client)
   * @param {string} culture - Nom de la culture
   * @returns {Object} Seuils par défaut
   */
  getDefaultThresholds(culture = 'generic') {
    const cultureLower = culture.toLowerCase();
    const thresholds = this.defaultThresholds[cultureLower] || this.defaultThresholds.generic;

    return {
      farmId: null,
      culture: cultureLower,
      objective: '',
      cropCoefficient: this.getCropCoefficient(cultureLower),
      isCoveredCrop: this.cropMetadata[cultureLower]?.isCoveredCrop ?? true,
      thresholds,
      customIndicators: {},
      isDefault: true
    };
  }

  /**
   * Liste les cultures disponibles avec seuils prédéfinis
   * @returns {Array} Liste des cultures
   */
  getAvailableCultures() {
    return Object.keys(this.defaultThresholds).map(key => ({
      id: key,
      name: key.charAt(0).toUpperCase() + key.slice(1),
      hasPresets: true,
      indicators: Object.keys(this.defaultThresholds[key])
    }));
  }

  /**
   * Récupère le coefficient cultural (Kc) pour une culture donnée
   * @param {string} culture - ID de la culture (ex: 'framboises', 'tomates')
   * @returns {number} Coefficient Kc (0.8 par défaut si culture inconnue)
   */
  getCropCoefficient(culture) {
    if (!culture || !this.cropMetadata[culture]) {
      console.warn(`[ThresholdsManager] Culture inconnue: ${culture}. Utilisation Kc par défaut: 0.8`);
      return this.cropMetadata.generic?.cropCoefficient || 0.8;
    }

    return this.cropMetadata[culture].cropCoefficient;
  }

  /**
   * Vérifie si une culture est sous protection (serre/tunnel)
   * @param {string} culture - ID de la culture
   * @returns {boolean} true si sous serre (défaut), false si plein champ
   */
  getIsCoveredCrop(culture) {
    if (!culture || !this.cropMetadata[culture]) {
      return this.cropMetadata.generic?.isCoveredCrop !== false;
    }

    return this.cropMetadata[culture].isCoveredCrop !== false;
  }

  // =========================================================================
  // SAUVEGARDE DES SEUILS (dans PostgreSQL via API)
  // =========================================================================

  /**
   * Sauvegarde ou met à jour les seuils pour une ferme dans PostgreSQL
   * @param {string|number} farmId - ID de la ferme
   * @param {Object} config - Configuration des seuils
   * @returns {Promise<boolean>} Succès de l'opération
   */
  async saveThresholds(farmId, config) {
    if (!farmId || !config) {
      console.error('Paramètres manquants pour sauvegarder les seuils');
      return false;
    }

    try {
      const dataToSave = {
        culture: config.culture || 'generic',
        objective: config.objective || '',
        cropCoefficient: config.cropCoefficient,
        isCoveredCrop: config.isCoveredCrop,
        thresholds: config.thresholds,
        customIndicators: config.customIndicators || {}
      };

      console.log('[ThresholdsManager] dataToSave:', JSON.stringify(dataToSave, null, 2));

      const response = await fetch(`${BackEndUrl}/api/farmroad/farms/${farmId}/settings`, {
        method: 'POST',
        headers: await this._headers(),
        body: JSON.stringify(dataToSave)
      });

      const result = await this._handleResponse(response, `POST /farms/${farmId}/settings`);

      if (result.success) {
        console.log('Seuils sauvegardés avec succès pour la ferme:', farmId);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Erreur sauvegarde seuils:', error);
      return false;
    }
  }

  /**
   * Réinitialise les seuils aux valeurs par défaut pour une culture
   * @param {string|number} farmId - ID de la ferme
   * @param {string} culture - Type de culture
   * @returns {Promise<boolean>} Succès de l'opération
   */
  async resetToDefaults(farmId, culture = 'generic') {
    const defaults = this.getDefaultThresholds(culture);
    return await this.saveThresholds(farmId, defaults);
  }

  // =========================================================================
  // VALIDATION DES SEUILS
  // =========================================================================

  /**
   * Valide que les seuils sont cohérents (min < optimal < max)
   * @param {Object} threshold - Seuil à valider
   * @returns {Object} Résultat de la validation
   */
  validateThreshold(threshold) {
    const errors = [];

    // Vérifier l'ordre croissant
    const values = [
      threshold.min_critical,
      threshold.min_warning,
      threshold.optimal_min,
      threshold.optimal_max,
      threshold.max_warning,
      threshold.max_critical
    ];

    for (let i = 0; i < values.length - 1; i++) {
      if (values[i] > values[i + 1]) {
        errors.push(`Valeur ${i} (${values[i]}) doit être <= valeur ${i + 1} (${values[i + 1]})`);
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  // =========================================================================
  // ÉVALUATION DES MESURES
  // =========================================================================

  /**
   * Évalue une mesure par rapport aux seuils configurés
   * @param {number} value - Valeur mesurée
   * @param {Object} threshold - Seuil de référence
   * @returns {Object} Évaluation de la mesure
   */
  evaluateMeasurement(value, threshold) {
    if (value === null || value === undefined) {
      return {
        severity: 'unknown',
        message: 'Valeur manquante',
        deviation: null
      };
    }

    // Critique bas
    if (value < threshold.min_critical) {
      return {
        severity: 'critical',
        message: `Critique : ${value} < ${threshold.min_critical} ${threshold.unit}`,
        deviation: threshold.min_critical - value,
        direction: 'low'
      };
    }

    // Avertissement bas
    if (value < threshold.min_warning) {
      return {
        severity: 'warning',
        message: `Avertissement : ${value} < ${threshold.min_warning} ${threshold.unit}`,
        deviation: threshold.min_warning - value,
        direction: 'low'
      };
    }

    // Zone optimale
    if (value >= threshold.optimal_min && value <= threshold.optimal_max) {
      return {
        severity: 'optimal',
        message: `Optimal : ${value} ${threshold.unit}`,
        deviation: 0,
        direction: null
      };
    }

    // Critique haut (vérifier EN PREMIER car plus sévère)
    if (value > threshold.max_critical) {
      return {
        severity: 'critical',
        message: `Critique : ${value} > ${threshold.max_critical} ${threshold.unit}`,
        deviation: value - threshold.max_critical,
        direction: 'high'
      };
    }

    // Avertissement haut (vérifier ENSUITE)
    if (value > threshold.max_warning) {
      return {
        severity: 'warning',
        message: `Avertissement : ${value} > ${threshold.max_warning} ${threshold.unit}`,
        deviation: value - threshold.max_warning,
        direction: 'high'
      };
    }

    // Entre optimal et warning (acceptable)
    return {
      severity: 'acceptable',
      message: `Acceptable : ${value} ${threshold.unit}`,
      deviation: 0,
      direction: value > threshold.optimal_max ? 'high' : 'low'
    };
  }

  /**
   * Évalue toutes les mesures agrégées par rapport aux seuils
   * @param {Object} aggregatedData - Données agrégées
   * @param {Object} thresholdsConfig - Configuration des seuils
   * @returns {Object} Évaluation complète
   */
  evaluateAggregatedData(aggregatedData, thresholdsConfig) {
    if (!aggregatedData || !thresholdsConfig || !thresholdsConfig.thresholds) {
      return {
        overall: { status: 'unknown', criticalCount: 0, warningCount: 0, optimalCount: 0 },
        byIndicator: {}
      };
    }

    const evaluations = {};
    let criticalCount = 0;
    let warningCount = 0;
    let optimalCount = 0;

    // Parcourir chaque indicateur
    Object.entries(aggregatedData).forEach(([indicatorKey, data]) => {
      // Normaliser la clé pour trouver le seuil correspondant
      const normalizedKey = this._normalizeIndicatorKey(indicatorKey);

      // Si null, c'est un indicateur technique à ignorer
      if (normalizedKey === null) {
        console.log(`Indicateur technique ignoré: "${indicatorKey}"`);
        return;
      }

      console.log(`Indicateur: "${indicatorKey}" → normalisé: "${normalizedKey}"`);

      // Trouver le seuil correspondant
      let threshold = thresholdsConfig.thresholds[normalizedKey];

      // Si aucun seuil trouvé, essayer avec les seuils par défaut
      if (!threshold) {
        console.log(`Aucun seuil trouvé pour "${normalizedKey}", tentative avec seuils par défaut...`);
        const defaultConfig = this.getDefaultThresholds(thresholdsConfig.culture || 'generic');
        threshold = defaultConfig.thresholds[normalizedKey];
      }

      // Déterminer si c'est un indicateur de lumière
      const isLightIndicator = ['RADIATION', 'PAR', 'LIGHT', 'SOLAR', 'LUMINOSITY'].some(keyword =>
        indicatorKey.toUpperCase().includes(keyword)
      );

      // Pour les indicateurs de lumière, utiliser la moyenne au lieu de latest
      // Car latest peut être une mesure de nuit (zéro)
      const representativeValue = isLightIndicator && data.intervals?.length > 0
        ? data.intervals.map(i => i.mean).reduce((sum, v) => sum + v, 0) / data.intervals.length
        : data.latest;

      if (!threshold) {
        console.warn(`Aucun seuil disponible pour l'indicateur "${indicatorKey}" (normalisé: "${normalizedKey}")`);
        evaluations[indicatorKey] = {
          severity: 'unknown',
          message: 'Seuils non configurés',
          latestValue: representativeValue,
          isLightIndicator,
          unit: data.unit || ''
        };
        return;
      }

      // Évaluer la valeur représentative
      const evaluation = this.evaluateMeasurement(representativeValue, threshold);

      evaluations[indicatorKey] = {
        ...evaluation,
        latestValue: representativeValue,
        isLightIndicator,
        unit: threshold.unit,
        threshold: {
          optimal: `${threshold.optimal_min}-${threshold.optimal_max} ${threshold.unit}`,
          critical_low: threshold.min_critical,
          critical_high: threshold.max_critical
        }
      };

      // Compter les statuts
      if (evaluation.severity === 'critical') criticalCount++;
      else if (evaluation.severity === 'warning') warningCount++;
      else if (evaluation.severity === 'optimal') optimalCount++;
    });

    // Déterminer le statut global
    let overallStatus = 'optimal';
    if (criticalCount > 0) overallStatus = 'critical';
    else if (warningCount > 0) overallStatus = 'warning';

    console.log(`Évaluation terminée: ${criticalCount} critiques, ${warningCount} warnings, ${optimalCount} optimaux`);

    return {
      overall: {
        status: overallStatus,
        criticalCount,
        warningCount,
        optimalCount,
        totalIndicators: Object.keys(evaluations).length
      },
      byIndicator: evaluations
    };
  }
}

// Export d'une instance singleton
export default new FarmRoadThresholdsManager();
